import { type ComponentType, type ReactNode, useContext } from "react";
import type { HeaderContext, RowData } from "@tanstack/react-table";
import { ChevronRightSmallIcon } from "@icons";
import type { DataGridRow } from "../columns/types";
import { useDataGridContext } from "../DataGridContext";
import type { DataGridFeatures } from "../features";
import { useIsRowExpanded } from "../grid/useRowState";
import { SummaryRowContext } from "./summaryRow";
import type { DataGridCellProps } from "./types";
import styles from "./cells.module.scss";

interface ExpandToggleProps<TData extends RowData> {
    row: DataGridRow<TData>;
    /** What a row with no children shows in the chevron's place. */
    slot?: ReactNode;
    attributes?: Partial<Record<`data-${string}`, string>>;
}

const TREE_SLOT_ATTRIBUTES = { "data-dt-tree-slot": "" };

/**
 * The row's chevron, or a spacer of the same width on a row with no children.
 * Out of the tab order; Left and Right collapse and expand from the keyboard.
 */
const ExpandToggle = <TData extends RowData>({
    row,
    slot,
    attributes,
}: Readonly<ExpandToggleProps<TData>>) => {
    const isExpanded = useIsRowExpanded(row);
    if (!row.getCanExpand()) {
        return (
            <span className={styles.expandSpacer} {...attributes}>
                {slot}
            </span>
        );
    }
    return (
        <button
            type="button"
            className={styles.expandButton}
            aria-label={isExpanded ? "Collapse row" : "Expand row"}
            data-expanded={isExpanded || undefined}
            tabIndex={-1}
            data-dt-interactive=""
            onClick={() => row.toggleExpanded()}
            {...attributes}
        >
            <ChevronRightSmallIcon aria-hidden />
        </button>
    );
};

/**
 * Chevron column for nested and group rows. The cell after it indents by
 * depth through `--dg-depth` on the row; a `tree` column indents its own
 * chevron instead (`TreeCell`).
 */
export const ExpandCell = <TData extends RowData>({
    context,
}: Readonly<DataGridCellProps<TData>>) => <ExpandToggle row={context.row} />;

interface TreeCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    value: ReactNode;
    /** `leadingPlacement: "toggleSlot"`: `leading`, shown in the spacer on rows that can't expand. */
    slot?: ComponentType<DataGridCellProps<TData>>;
}

/**
 * A `tree` column's cell: the chevron (or its spacer) then the value, both
 * indented one chevron per level, so a child's chevron sits under its
 * parent's label and leaf rows line up with their siblings' labels. Summary
 * rows leave the spacer empty.
 */
export const TreeCell = <TData extends RowData>({
    context,
    meta,
    value,
    slot: Slot,
}: Readonly<TreeCellProps<TData>>) => {
    const isSummaryRow = useContext(SummaryRowContext);
    return (
        <span className={styles.treeCell}>
            <ExpandToggle
                row={context.row}
                slot={
                    Slot && !isSummaryRow ? (
                        <Slot context={context} meta={meta} />
                    ) : null
                }
                attributes={TREE_SLOT_ATTRIBUTES}
            />
            <span className={styles.treeValue} data-dt-tree-value="">
                {value}
            </span>
        </span>
    );
};

interface ExpandAllHeaderProps<TData extends RowData> {
    context: HeaderContext<DataGridFeatures, TData>;
}

/**
 * The grid's expand state for header parts. The header context's table
 * object stays the same as rows load and expand, so a compiled header that
 * read it would keep its first answer; `visit` hands over the current table.
 */
const useTreeExpansion = () =>
    useDataGridContext().visit(({ table }) => ({
        canSomeRowsExpand: table.getCanSomeRowsExpand(),
        isSomeRowsExpanded: table.getIsSomeRowsExpanded(),
    }));

/**
 * Expand all / collapse all. While any row is expanded it collapses all, so
 * collapsing one group leaves it alone.
 */
export const ExpandAllHeader = <TData extends RowData>({
    context,
}: Readonly<ExpandAllHeaderProps<TData>>) => {
    const { canSomeRowsExpand, isSomeRowsExpanded: isSomeExpanded } =
        useTreeExpansion();
    if (!canSomeRowsExpand) {
        return null;
    }
    return (
        <button
            type="button"
            className={styles.expandButton}
            aria-label={
                isSomeExpanded ? "Collapse all rows" : "Expand all rows"
            }
            data-expanded={isSomeExpanded || undefined}
            tabIndex={-1}
            data-dt-interactive=""
            onClick={() => context.table.toggleAllRowsExpanded(!isSomeExpanded)}
        >
            <ChevronRightSmallIcon aria-hidden />
        </button>
    );
};

/** Holds a chevron's width in a `tree` header, so its label sits over the row labels. */
export const TreeHeaderSpacer = () => <span className={styles.expandSpacer} />;

/**
 * A `tree` column header's lead with `expandAll`: expand all, or the spacer.
 * The header renders it beside its sort button, never inside, so toggling
 * leaves the sort alone.
 */
export const TreeHeaderToggle = <TData extends RowData>({
    context,
}: Readonly<ExpandAllHeaderProps<TData>>) =>
    useTreeExpansion().canSomeRowsExpand ? (
        <ExpandAllHeader context={context} />
    ) : (
        <TreeHeaderSpacer />
    );
